import React, { useState } from 'react';
import {
    ArrowLeft, Server, Loader2, Search, ExternalLink, Sparkles, ShieldCheck,
    ShieldAlert, ShieldX, AlertCircle
} from 'lucide-react';
import { scoreJobTrust } from '../jobTrust.js';

// Career-ops's #1 differentiator is its 45+ portal scanners. Architect's
// scoped equivalent: query the documented public job-board APIs for the
// four biggest ATS systems used in tech. No scraping, no Playwright, no
// ToS issue — these endpoints are intended for embedding company job
// boards on partner sites.

const PROVIDERS = [
    {
        id: 'greenhouse',
        name: 'Greenhouse',
        hint: 'e.g. airbnb, stripe, vercel',
        howTo: 'Find the slug in the company\'s career-page URL: `boards.greenhouse.io/{slug}` or `{slug}.greenhouse.io`'
    },
    {
        id: 'lever',
        name: 'Lever',
        hint: 'e.g. netflix, mozilla, shopify',
        howTo: 'Find the slug in `jobs.lever.co/{slug}`'
    },
    {
        id: 'ashby',
        name: 'Ashby',
        hint: 'e.g. linear, ramp, openai',
        howTo: 'Find the slug in `jobs.ashbyhq.com/{slug}`'
    },
    {
        id: 'workable',
        name: 'Workable',
        hint: 'e.g. canonical, ebay',
        howTo: 'Find the slug in `apply.workable.com/{slug}`'
    }
];

const TRUST_ICONS = { trusted: ShieldCheck, caution: ShieldAlert, suspicious: ShieldX };
const TRUST_TEXT = { trusted: 'text-emerald-700', caution: 'text-amber-700', suspicious: 'text-red-700' };
const TRUST_BG = { trusted: 'bg-emerald-50', caution: 'bg-amber-50', suspicious: 'bg-red-50' };

const PortalScanner = ({ onBack, onAnalyzeJob, showNotification }) => {
    const [provider, setProvider] = useState('greenhouse');
    const [slug, setSlug] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');

    const currentProvider = PROVIDERS.find(p => p.id === provider);

    const handleScan = async (e) => {
        e?.preventDefault();
        if (!slug.trim()) return;
        setLoading(true);
        setError(null);
        setResults(null);
        try {
            const url = `/api/scan-portal?provider=${encodeURIComponent(provider)}&slug=${encodeURIComponent(slug.trim())}`;
            const r = await fetch(url);
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || `Scan failed (${r.status})`);
            setResults(data);
            if (data.count === 0) {
                showNotification?.(`No open roles found at ${slug} on ${currentProvider.name}.`);
            } else {
                showNotification?.(`Found ${data.count} role${data.count === 1 ? '' : 's'} at ${slug}.`);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredJobs = results?.jobs?.filter(j =>
        !filter || `${j.job_title} ${j.job_city} ${j.job_country}`.toLowerCase().includes(filter.toLowerCase())
    ) || [];

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-2">
                    <Server className="text-blue-600" size={20} />
                    <h1 className="font-bold text-slate-800">ATS Portal Scanner</h1>
                </div>
            </header>

            <div className="max-w-5xl mx-auto p-6 space-y-4">
                {/* Provider picker */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                    <p className="text-xs font-bold text-slate-500 uppercase mb-3">Pick a portal</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        {PROVIDERS.map(p => (
                            <button
                                key={p.id}
                                onClick={() => setProvider(p.id)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${provider === p.id
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                            >
                                {p.name}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={handleScan} className="space-y-2">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={slug}
                                onChange={e => setSlug(e.target.value)}
                                placeholder={`Company slug — ${currentProvider.hint}`}
                                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <button
                                type="submit"
                                disabled={!slug.trim() || loading}
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2"
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                Scan
                            </button>
                        </div>
                        <p className="text-xs text-slate-500">{currentProvider.howTo}</p>
                    </form>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                        <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                        <div>
                            <p className="font-bold text-red-800 text-sm">Scan failed</p>
                            <p className="text-xs text-red-700 mt-1">{error}</p>
                        </div>
                    </div>
                )}

                {/* Results */}
                {results && results.jobs && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600">
                                <strong className="text-slate-800">{results.count}</strong> role{results.count === 1 ? '' : 's'} at <strong>{results.slug}</strong> via {results.providerName}
                            </span>
                            {results.count > 0 && (
                                <input
                                    type="text"
                                    value={filter}
                                    onChange={e => setFilter(e.target.value)}
                                    placeholder="Filter by title or location…"
                                    className="ml-auto text-xs border border-slate-200 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none w-64"
                                />
                            )}
                        </div>

                        {filteredJobs.length === 0 && results.count > 0 && (
                            <p className="text-sm text-slate-500 text-center py-6">No roles match "{filter}".</p>
                        )}

                        {filteredJobs.map((job, i) => {
                            const trust = scoreJobTrust(job);
                            const TrustIcon = TRUST_ICONS[trust.level];
                            return (
                                <div key={`${job.job_provider}-${job.job_id}-${i}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <h3 className="font-bold text-slate-800">{job.job_title}</h3>
                                                <span className={`inline-flex items-center gap-1 ${TRUST_BG[trust.level]} ${TRUST_TEXT[trust.level]} px-2 py-0.5 rounded-full text-[10px] font-bold uppercase`}>
                                                    <TrustIcon size={10} /> {trust.label} · {trust.score}
                                                </span>
                                            </div>
                                            <p className="text-sm text-slate-600">
                                                {job.employer_name}
                                                {job.job_city && <span className="text-slate-400"> · {job.job_city}{job.job_country ? `, ${job.job_country}` : ''}</span>}
                                                {job.job_is_remote && <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Remote</span>}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500 line-clamp-2 mb-3">{job.job_description || '(No description in API response — open the posting for details.)'}</p>
                                    <div className="flex items-center gap-2">
                                        <a
                                            href={job.job_apply_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md flex items-center gap-1"
                                        >
                                            Open <ExternalLink size={11} />
                                        </a>
                                        <button
                                            onClick={() => onAnalyzeJob({
                                                title: job.job_title,
                                                company: job.employer_name,
                                                description: job.job_description,
                                                link: job.job_apply_link
                                            })}
                                            className="text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-md flex items-center gap-1"
                                        >
                                            <Sparkles size={11} /> Analyze
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!results && !loading && !error && (
                    <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
                        <Server size={36} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-sm text-slate-600 mb-1">Scan a company's ATS directly.</p>
                        <p className="text-xs text-slate-400 max-w-md mx-auto">
                            Most tech companies host their public job board on Greenhouse, Lever, Ashby, or Workable. This bypasses third-party job aggregators and queries the source-of-truth API, so listings are fresher and complete.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PortalScanner;
